import { createEvent, createStore, restore, sample } from 'effector'
import { createGate } from 'effector-react'
import {
  AddWinnersRequest,
  Winner,
  WinnersResponse,
  WorldCupStatus,
  WorldCupStatusResponse,
} from './types'
import { fx } from 'src/libs'
import { buildQuery } from 'src/pages/worldCup2026/utils'

import { BASE_URL, PAGE_SIZE } from 'src/pages/worldCup2026/const'
import { ListRequest } from 'src/pages/worldCup2026/types'
import {
  NotificationPayload,
  showNotification,
} from 'src/features/notifications'

export const WinnerListGate = createGate()

export const onChangeWinnerList = createEvent<ListRequest>()
export const changeStatusSubmit = createEvent<void | any>()
export const showError = createEvent<Error>()

export const getWorldCupStatusFx = fx<void, WorldCupStatusResponse>({
  method: 'GET',
  url: `${BASE_URL}promo`,
})

export const changeStatusFx = fx({
  method: 'PUT',
  url: `${BASE_URL}promo/{id}`,
})

export const getWinnersFx = fx<ListRequest, WinnersResponse>({
  method: 'GET',
  url: `${BASE_URL}winner`,
})

export const addWinnersFx = fx<AddWinnersRequest, void>({
  method: 'POST',
  url: `${BASE_URL}winner/bulk`,
})

export const $worldCupStatus = createStore<WorldCupStatus[]>([]).on(
  getWorldCupStatusFx.doneData,
  (_, res) => res.body.items,
)

export const $winners = createStore<Winner[]>([]).on(
  getWinnersFx.doneData,
  (_, res) => res.body.items,
)

export const $totalCountOfWinners = restore(
  getWinnersFx.doneData.map((a) => a.body.total),
  0,
)

export const $winnersParams = createStore<ListRequest>({
  page: 1,
  limit: PAGE_SIZE,
}).on(onChangeWinnerList, (state, payload) => ({ ...state, ...payload }))

sample({
  clock: WinnerListGate.open,
  fn: () => ({}),
  target: getWorldCupStatusFx,
})

sample({
  clock: WinnerListGate.open,
  fn: () => ({
    query: { page: '1', limit: String(PAGE_SIZE) },
  }),
  target: getWinnersFx,
})

sample({
  clock: onChangeWinnerList,
  fn: buildQuery,
  target: getWinnersFx,
})

sample({
  clock: changeStatusSubmit,
  fn: () => ({
    url: `${BASE_URL}promo/1`,
    body: { status: 'results' },
  }),
  target: changeStatusFx,
})

sample({
  clock: changeStatusFx.done,
  fn: () => [{ status: 'ended' }],
  target: $worldCupStatus,
})

sample({
  clock: addWinnersFx.doneData,
  source: $winnersParams,
  fn: buildQuery,
  target: getWinnersFx,
})

sample({
  clock: changeStatusFx.doneData,
  fn: (): NotificationPayload => ({
    type: 'success',
    message: 'Success',
    description: 'Status changed to results',
  }),
  target: showNotification,
})

sample({
  clock: addWinnersFx.doneData,
  fn: (): NotificationPayload => ({
    type: 'success',
    message: 'Success',
    description: 'Winner added',
  }),
  target: showNotification,
})

sample({
  clock: showError,
  fn: (error): NotificationPayload => ({
    type: 'error',
    message: 'File error',
    description: error.message,
  }),
  target: showNotification,
})

sample({
  clock: [
    getWorldCupStatusFx.failData,
    getWinnersFx.failData,
    changeStatusFx.failData,
    addWinnersFx.failData,
  ],
  fn: (error): NotificationPayload => ({
    type: 'error',
    message: 'Error',
    description: error?.body?.message ?? 'Request failed',
  }),
  target: showNotification,
})
